import { Box, Button, Divider, Group, Modal, Stack, Switch, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useQueryClient } from '@tanstack/react-query';
import {
  HARC_UPDATE_BOUNDARY_JST,
  getHarcDataDate,
  msUntilNextHarcBoundary,
} from '../../config/harcCache';
import { QUERY_PERSIST_KEY } from '../../config/queryClient';
import { useAuthStore } from '../../stores/authStore';
import { useDebugStore } from '../../stores/debugStore';
import { Role } from '../../types/user';

interface DebugModalProps {
  /** Modal の開閉状態 */
  opened: boolean;
  /** Modal を閉じるハンドラ */
  onClose: () => void;
}

/**
 * 次の HARC 境界時刻を 'YYYY-MM-DD HH:MM JST' 形式で返す。
 *
 * @returns 次回境界時刻の人間可読表記
 */
function formatNextBoundary(): string {
  const ms = msUntilNextHarcBoundary();
  const next = new Date(Date.now() + ms);
  // JST 表示（UTC+9 に変換した値を UTC 風に取り出して整形）
  const jst = new Date(next.getTime() + 9 * 60 * 60 * 1000);
  const yyyy = jst.getUTCFullYear();
  const mm = String(jst.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(jst.getUTCDate()).padStart(2, '0');
  const hh = String(jst.getUTCHours()).padStart(2, '0');
  const mi = String(jst.getUTCMinutes()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd} ${hh}:${mi} JST`;
}

/**
 * 境界時刻の設定値を 'HH:MM JST' 形式で返す。
 *
 * @returns 境界時刻の人間可読表記
 */
function formatBoundaryConfig(): string {
  const { hour, minute } = HARC_UPDATE_BOUNDARY_JST;
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')} JST`;
}

/**
 * Modal 内の情報行（ラベル：値）を 1 行表示するユーティリティ。
 */
function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Group justify="space-between" wrap="nowrap" gap="md">
      <Text size="sm" c="dimmed" style={{ flexShrink: 0 }}>
        {label}
      </Text>
      <Text size="sm" style={{ textAlign: 'right', wordBreak: 'break-all' }}>
        {value}
      </Text>
    </Group>
  );
}

/**
 * デバッグ Modal
 *
 * ヘッダーのユーザーメニューから開く運用設定 / 状態確認画面。
 * メニュー項目（LeftPanel）が ADMIN 限定のため、本 Modal も実質 ADMIN のみが開ける。
 *
 * 機能:
 * - アプリ情報（バージョン / 環境 / ビルド時刻）の表示
 * - HARC API キャッシュの ON/OFF トグル + 状態表示 + キャッシュクリア
 * - ログインユーザー情報の表示
 *
 * @param props - opened: 開閉状態 / onClose: 閉じるハンドラ
 */
export function DebugModal({ opened, onClose }: DebugModalProps) {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const harcCacheEnabled = useDebugStore((s) => s.harcCacheEnabled);
  const setHarcCacheEnabled = useDebugStore((s) => s.setHarcCacheEnabled);

  /**
   * HARC キャッシュ（in-memory + localStorage）を完全に破棄する共通処理。
   *
   * - resetQueries: active observers の表示状態を初期化し、即時 refetch を発火させる
   *   （removeQueries だけだと active 観察者が古いデータを保持し続けてしまう）
   * - removeQueries: QueryInstance を物理破棄する。resetQueries は instance を残すため
   *   作成時の gcTime（24h）が焼き付いたまま、観察者アンマウント後 24h 残留して
   *   再表示時にキャッシュ再利用が起きてしまうのを防ぐ
   * - localStorage.removeItem: persister の throttle や dehydrate の判定に頼らず
   *   永続化キャッシュを同期的に削除し、直後のリロードで古い hydrate が起きないようにする
   */
  const purgeHarcCache = () => {
    queryClient.resetQueries({ queryKey: ['harc'] });
    queryClient.removeQueries({ queryKey: ['harc'] });
    try {
      localStorage.removeItem(QUERY_PERSIST_KEY);
    } catch {
      // localStorage 利用不可環境では何もしない（persister 側で no-op フォールバック）
    }
  };

  const handleToggle = (enabled: boolean) => {
    setHarcCacheEnabled(enabled);
    if (!enabled) {
      purgeHarcCache();
      notifications.show({
        title: 'HARC キャッシュを無効化しました',
        message: '既存のキャッシュを破棄しました。以降は毎回サーバーから取得します。',
        color: 'yellow',
      });
    } else {
      notifications.show({
        title: 'HARC キャッシュを有効化しました',
        message: '次の境界時刻まで再取得を抑止します。',
        color: 'green',
      });
    }
  };

  const handleClearCache = () => {
    purgeHarcCache();
    notifications.show({
      title: 'キャッシュをクリアしました',
      message: 'HARC API のキャッシュを破棄しました。表示中の画面は再取得されます。',
      color: 'green',
    });
  };

  return (
    <Modal opened={opened} onClose={onClose} title="🛠 デバッグ" size="md" centered>
      <Stack gap="md">
        <Box>
          <Text fw={600} size="sm" mb="xs">
            アプリ情報
          </Text>
          <Stack gap={4}>
            <InfoRow label="バージョン" value={`v${__APP_VERSION__}`} />
            <InfoRow label="環境" value={import.meta.env.MODE} />
            <InfoRow label="ビルド" value={__BUILD_TIME__} />
          </Stack>
        </Box>

        <Divider />

        <Box>
          <Text fw={600} size="sm" mb="xs">
            HARC API キャッシュ
          </Text>
          <Stack gap="sm">
            <Switch
              label="キャッシュを有効化"
              checked={harcCacheEnabled}
              onChange={(e) => handleToggle(e.currentTarget.checked)}
            />
            <Stack gap={4}>
              <InfoRow label="現在のデータ日付" value={getHarcDataDate()} />
              <InfoRow label="次回境界時刻" value={formatNextBoundary()} />
              <InfoRow label="境界時刻設定" value={formatBoundaryConfig()} />
            </Stack>
            <Button variant="outline" color="red" size="sm" onClick={handleClearCache}>
              キャッシュをクリア
            </Button>
          </Stack>
        </Box>

        {/* ユーザー情報は ADMIN のみ表示（PoC 期間中の運用判断） */}
        {user?.role === Role.ADMIN && (
          <>
            <Divider />
            <Box>
              <Text fw={600} size="sm" mb="xs">
                ユーザー情報
              </Text>
              <Stack gap={4}>
                <InfoRow label="UID" value={user.uid} />
                <InfoRow label="名前" value={user.name || '-'} />
                <InfoRow label="Email" value={user.email || '-'} />
                <InfoRow label="Role" value={user.role} />
              </Stack>
            </Box>
          </>
        )}
      </Stack>
    </Modal>
  );
}
